/** Read only the upload's small header before attempting an optional browser thumbnail. */
const readDimensions = (header: ArrayBuffer): { width: number, height: number } | undefined => {
  const bytes = new Uint8Array(header);
  const view = new DataView(header);
  const text = (offset: number, length: number) => String.fromCharCode(...bytes.subarray(offset, offset + length));
  const uint24 = (offset: number) => bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16);
  if (bytes.length >= 24 && text(0, 8) === '\x89PNG\r\n\x1a\n') {
    return { width: view.getUint32(16), height: view.getUint32(20) };
  }
  if (bytes.length >= 10 && ['GIF87a', 'GIF89a'].includes(text(0, 6))) {
    return { width: view.getUint16(6, true), height: view.getUint16(8, true) };
  }
  if (bytes.length >= 26 && text(0, 2) === 'BM') {
    const dibSize = view.getUint32(14, true);
    if (dibSize === 12) return { width: view.getUint16(18, true), height: view.getUint16(20, true) };
    if (dibSize >= 40) return { width: view.getInt32(18, true), height: Math.abs(view.getInt32(22, true)) };
  }
  if (bytes.length >= 30 && text(0, 4) === 'RIFF' && text(8, 4) === 'WEBP') {
    switch (text(12, 4)) {
      case 'VP8X':
        return { width: 1 + uint24(24), height: 1 + uint24(27) };
      case 'VP8 ':
        if (text(23, 3) === '\x9d\x01\x2a') return { width: view.getUint16(26, true) & 0x3fff, height: view.getUint16(28, true) & 0x3fff };
        break;
      case 'VP8L':
        if (bytes[20] === 0x2f) {
          const bits = view.getUint32(21, true);
          return { width: 1 + (bits & 0x3fff), height: 1 + ((bits >>> 14) & 0x3fff) };
        }
    }
  }
  if (bytes.length >= 4 && view.getUint16(0) === 0xffd8) {
    let offset = 2;
    while (offset + 4 <= bytes.length) {
      if (bytes[offset++] !== 0xff) return undefined;
      while (offset < bytes.length && bytes[offset] === 0xff) offset++;
      if (offset + 3 > bytes.length) return undefined;
      const marker = bytes[offset++];
      if (marker === 0xda || marker === 0xd9) return undefined;
      if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > bytes.length) return undefined;
      if (marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker) && length >= 8) {
        return { width: view.getUint16(offset + 5), height: view.getUint16(offset + 3) };
      }
      offset += length;
    }
  }
  return undefined;
};

export const canPreviewImage = (header: ArrayBuffer): boolean => {
  const size = readDimensions(header);
  // A cover thumbnail can upscale the long axis enormously for a one-pixel strip.
  // Large or unusual files still upload normally and receive a server preview.
  return size !== undefined && size.width > 0 && size.height > 0
    && size.width * size.height <= 16_000_000
    && Math.max(size.width, size.height) / Math.min(size.width, size.height) <= 32;
};
